/**
 * Bhrigu Nandi Nadi (BNN) — independent career-report page.
 *
 * BNN is intentionally simple: Saturn-as-karaka, its house, its
 * conjunctions/aspect, Jupiter's growth direction, Bhrigu Bindu.
 * No yogas, no Shadbala, no Ashtakavarga, no D-9/D-10 corroboration.
 * The whole report fits on one screen and reads in under a minute.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { BnnReportResponse, ChartCreateRequest } from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function BnnReportPage() {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [submittedReq, setSubmittedReq] = useState<ChartCreateRequest | null>(null);

  const reportMut = useMutation<BnnReportResponse, Error, ChartCreateRequest>({
    mutationFn: (req) => api.bnnReport(req),
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      setSubmittedReq(req);
      reportMut.mutate(req);
    },
    [reportMut],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
      if (e.status === 401) return t('फिर से प्रवेश करें', 'Please sign in again');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <header className="rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <h1 className="text-lg font-semibold text-saffron-100">
          {t('भृगु नंदी नाडी — सरल करियर परामर्श', 'Bhrigu Nandi Nadi — Simple Career Reading')}
        </h1>
        <p className="mt-1 text-xs text-stone-400">
          {t(
            'शनि आधारित — एक सीधा, संक्षिप्त परामर्श (1 मिनट का पठन)। भारी विवरण के लिए मुख्य रिपोर्ट देखें।',
            'Saturn-centric — a direct, concise reading (1-minute read). Use the main report for the detailed view.',
          )}
        </p>
      </header>

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />

      {!submittedReq && !reportMut.isPending && (
        <section className="rounded-2xl bg-gradient-to-br from-saffron-900/30 to-stone-900 p-6 text-center ring-1 ring-saffron-700/30">
          <p className="text-lg font-semibold text-saffron-200">
            {t('जन्म विवरण भरें', 'Enter birth details')}
          </p>
          <p className="mt-2 text-sm text-stone-400">
            {t(
              'BNN पद्धति केवल जन्म-कुण्डली (D-1) पर आधारित है — कोई वर्ग कुण्डली, कोई योग-तालिका, कोई षड्बल नहीं।',
              'BNN works on the natal chart (D-1) alone — no divisional charts, no yoga tables, no Shadbala.',
            )}
          </p>
        </section>
      )}

      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('गणना हो रही है…', 'Computing…')}
        </p>
      )}

      {report && <BnnReportView report={report} lang={lang} t={t} />}
    </div>
  );
}

// ─── Report card ────────────────────────────────────────────────────────────

function BnnReportView({
  report,
  lang,
  t,
}: {
  report: BnnReportResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  const summary = lang === 'hi' ? report.summary_hi : report.summary_en;
  const karmicField = lang === 'hi' ? report.karmic_field_hi : report.karmic_field_en;
  const jupiterGrowth = lang === 'hi' ? report.jupiter_growth_hi : report.jupiter_growth_en;

  return (
    <section className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-saffron-900/40 to-stone-900 p-5 ring-1 ring-saffron-700/40">
        <p className="text-xs uppercase tracking-wider text-saffron-400">
          {t('संक्षेप', 'Summary')}
        </p>
        <p className="mt-2 text-base leading-relaxed text-stone-100">{summary}</p>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Tile
          label={t('शनि — कर्म-कारक', 'Saturn — karma-karaka')}
          value={`${t('भाव', 'House')} ${report.saturn_house}`}
          sub={lang === 'hi' ? report.saturn_rashi_hi : report.saturn_rashi_en}
        />
        <Tile
          label={t('गुरु — विकास-दिशा', 'Jupiter — direction of growth')}
          value={
            report.jupiter_house
              ? `${t('भाव', 'House')} ${report.jupiter_house}`
              : '—'
          }
          sub={jupiterGrowth.replace(/।$/, '').replace(/\.$/, '')}
        />
        <Tile
          label={t('भृगु बिन्दु', 'Bhrigu Bindu')}
          value={
            lang === 'hi' ? report.bhrigu_bindu_rashi_hi : report.bhrigu_bindu_rashi_en
          }
          sub={`${t('भाव', 'House')} ${report.bhrigu_bindu_house} · ${report.bhrigu_bindu_longitude.toFixed(2)}°`}
        />
        <Tile
          label={t('शनि का राशि-स्थान', "Saturn's sign placement")}
          value={lang === 'hi' ? report.saturn_rashi_hi : report.saturn_rashi_en}
          sub={karmicField.split('—')[1]?.trim() || karmicField}
        />
      </div>

      {report.conjunctions.length > 0 && (
        <div className="rounded-2xl bg-stone-950/40 p-4 ring-1 ring-stone-800 lg:p-5">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-saffron-400">
            {t('शनि के साथ ग्रह-संयोग एवं दृष्टि', 'Saturn — conjunctions and aspects')}
          </h3>
          <ul className="space-y-2">
            {report.conjunctions.map((c) => (
              <li
                key={c.planet_id}
                className="rounded-md border-l-2 border-saffron-700/60 bg-stone-900/40 px-3 py-2"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium text-stone-100">
                    {lang === 'hi' ? c.planet_hi : c.planet_en}
                  </span>
                  <span className="text-[11px] uppercase tracking-wider text-saffron-300">
                    {c.relation === 'conjunct'
                      ? t('युति', 'conjunct')
                      : t('सप्तम-दृष्टि', '7th aspect')}
                  </span>
                  <span className="text-[11px] text-stone-500">
                    {t('भाव', 'House')} {c.house}
                  </span>
                </div>
                <p className="mt-1 text-sm text-stone-300">
                  {lang === 'hi' ? c.flavour_hi : c.flavour_en}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.suggestions.length > 0 && (
        <div className="rounded-2xl bg-stone-950/40 p-4 ring-1 ring-stone-800 lg:p-5">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-saffron-400">
            {t('सुझाये गए क्षेत्र', 'Suggested fields')}
          </h3>
          <ol className="space-y-2 text-sm text-stone-100">
            {report.suggestions.map((s, i) => (
              <li key={i} className="rounded-md bg-stone-900/40 px-3 py-2">
                <p>{lang === 'hi' ? s.field_hi : s.field_en}</p>
                <p className="mt-1 text-[11px] text-stone-500">
                  {lang === 'hi' ? s.why_hi : s.why_en}
                </p>
              </li>
            ))}
          </ol>
        </div>
      )}

      <p className="text-center text-[11px] text-stone-500">
        {t(
          'BNN केवल जन्म-कुण्डली पर आधारित — विवरण के लिए "करियर रिपोर्ट" पर जाएँ।',
          'BNN reads from the natal chart only — switch to "Career Report" for the full analysis.',
        )}
      </p>
    </section>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-2xl bg-stone-900/60 p-4 ring-1 ring-stone-800">
      <p className="text-[10px] uppercase tracking-wider text-saffron-400">{label}</p>
      <p className="mt-1 text-base font-semibold text-stone-100">{value}</p>
      <p className="mt-1 text-[12px] leading-snug text-stone-400">{sub}</p>
    </div>
  );
}
